import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/utils/apiClient";
import { useAuth } from "@/state/auth";

export type CompSeverity = "violation" | "review" | "info";

export interface CompCheckInfo {
  check_id: string;
  title: string;
  severity: CompSeverity;
  description: string;
}

export interface CompRunSummary {
  checks?: Record<string, { count: number; severity: CompSeverity; title: string }>;
  config_drift_tables?: number;
  totals?: {
    violation_findings: number;
    review_findings: number;
    info_findings: number;
  };
  window_start?: string;
  sample_limit?: number;
}

export interface CompRun {
  id: number;
  started_at: string;
  finished_at: string | null;
  status: "running" | "clean" | "review" | "violations" | "failed";
  window_days: number;
  triggered_by: string | null;
  summary: CompRunSummary | null;
  error: string | null;
}

export interface CompFinding {
  id: number;
  run_id: number;
  check_id: string;
  severity: CompSeverity;
  title: string | null;
  member_id: string | null;
  payment_type: string | null;
  amount: string | null;
  occurred_on: string | null;
  detail: Record<string, unknown> | null;
  created_at: string;
}

export const useCompChecks = () => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: ["comp-health", "checks"],
    queryFn: () => apiRequest<{ checks: CompCheckInfo[] }>("/api/admin/comp-health/checks"),
    staleTime: 5 * 60_000,
    enabled: Boolean(profile),
  });
};

export const useCompRuns = () => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: ["comp-health", "runs"],
    queryFn: () => apiRequest<{ runs: CompRun[] }>("/api/admin/comp-health/runs?limit=25"),
    staleTime: 15_000,
    enabled: Boolean(profile),
  });
};

export interface CompMember {
  user_id: number;
  username: string | null;
  member_code: string | null;
  name: string;
  country: string | null;
  rank_id: number | null;
  rank_name: string | null;
  active: boolean | null;
}

export interface FindingFilters {
  check_id?: string;
  severity?: CompSeverity;
  limit?: number;
  offset?: number;
}

export const useCompFindings = (runId: number | null, filters: FindingFilters) => {
  const profile = useAuth((state) => state.profile);
  const params = new URLSearchParams();
  if (filters.check_id) params.set("check_id", filters.check_id);
  if (filters.severity) params.set("severity", filters.severity);
  params.set("limit", String(filters.limit ?? 100));
  params.set("offset", String(filters.offset ?? 0));
  return useQuery({
    queryKey: ["comp-health", "findings", runId, filters],
    queryFn: () =>
      apiRequest<{ findings: CompFinding[]; members?: Record<string, CompMember>; count: number }>(
        `/api/admin/comp-health/runs/${runId}/findings?${params.toString()}`
      ),
    enabled: Boolean(profile) && runId !== null,
    staleTime: 15_000,
  });
};

export const useStartCompRun = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { window_days?: number; sample_limit?: number }) =>
      apiRequest<CompRunSummary & { run_id: number; status: string }>(
        "/api/admin/comp-health/runs",
        { method: "POST", body: JSON.stringify(body) }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comp-health", "runs"] });
    },
  });
};

export interface ExplainResult {
  finding_id: number;
  explanation: string;
  model: string | null;
  cached: boolean;
  feedback: { up: number; down: number };
}

export const useExplainFinding = () =>
  useMutation({
    mutationFn: ({ findingId, force }: { findingId: number; force?: boolean }) =>
      apiRequest<ExplainResult>(
        `/api/admin/comp-health/findings/${findingId}/explain`,
        { method: "POST", body: JSON.stringify({ force: !!force }) }
      ),
  });

export const useExplainFeedback = () =>
  useMutation({
    mutationFn: ({ findingId, rating, comment }: { findingId: number; rating: "up" | "down"; comment?: string }) =>
      apiRequest<{ ok: boolean; feedback: { up: number; down: number } }>(
        `/api/admin/comp-health/findings/${findingId}/explain/feedback`,
        { method: "POST", body: JSON.stringify({ rating, comment: comment || undefined }) }
      ),
  });
